import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  activePreset,
  formatShortDate,
  isoDate,
  PERIOD_PRESET_NAME_ID,
  PeriodChoice,
  periodChoiceOf,
  periodNeedsYear,
  presetRange,
  rangeTextOf,
} from '../../components/novarium/periodRange'
import { PeriodDateField } from '../../components/novarium/PeriodField'
import { OperatingRoomFilterDTO } from '../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { parseIsoDate } from '../ServicesStatistics/statisticsUtils'
import {
  OperatingRoomFiltersState,
  OperatingRoomSelectFilters,
  NO_SELECT_FILTERS,
  sameApplied,
  toAppliedFilters,
} from './operatingRoomFilterModel'

/** El período que había antes de «Ver solo este día» y el rango al que se pasó (`yyyy-MM-dd`). */
interface RangeOrigin {
  previousFrom: Date | null
  previousTo: Date | null
  /** El período anterior era «Personalizado». */
  custom: boolean
  /** Cómo se llamaba el período anterior («Mes actual», «1/9 al 30/9»), para «Volver a …». */
  label: string
  from: string
  to: string
}

export type OperatingRoomDateErrors = Partial<Record<PeriodDateField, 'required' | 'order'>>

/** Cuánto se espera antes de consultar cuando los combos cambian seguido. */
const DEBOUNCE_MS = 300

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

function validate(filters: OperatingRoomFiltersState): OperatingRoomDateErrors {
  const errors: OperatingRoomDateErrors = {}
  if (!filters.from) errors.from = 'required'
  if (!filters.to) errors.to = 'required'
  else if (filters.from && startOfDay(filters.from) > startOfDay(filters.to)) errors.to = 'order'
  return errors
}

const sameErrors = (a: OperatingRoomDateErrors, b: OperatingRoomDateErrors): boolean => a.from === b.from && a.to === b.to

/**
 * Filtros de la pantalla, todos al instante (no hay «Consultar»). El período, las fechas y los chips se aplican ya; los
 * combos, después de `DEBOUNCE_MS` (pueden cambiar seguidos). La pantalla abre en «Mes actual», como Atenciones de urgencia
 * e Indicadores de internación. Las fechas se validan: un error sale en su campo y no se consulta.
 *
 * `filters` es lo que se está editando y `applied` lo que se está consultando; `pending` avisa que hay un cambio
 * esperando el retardo, así los datos que se ven ya no son los de los filtros. Un pedido en vuelo lo cancela quien
 * consulta (`useOperatingRoomSummary` y la tabla), no este hook.
 */
export default function useOperatingRoomFilters() {
  const intl = useIntl()
  const today = useMemo(() => new Date(), [])

  const [filters, setFilters] = useState<OperatingRoomFiltersState>(() => {
    const { from, to } = presetRange('thisMonth', today)
    return { from, to, ...NO_SELECT_FILTERS }
  })
  const filtersRef = useRef(filters)
  const [errors, setErrors] = useState<OperatingRoomDateErrors>({})
  /** Eligió «Personalizado» en «Período»: las fechas se muestran aunque coincidan con un atajo. */
  const [customPeriod, setCustomPeriod] = useState(false)
  const [applied, setApplied] = useState<OperatingRoomFilterDTO>(() => toAppliedFilters(filters)!)
  const appliedRef = useRef(applied)
  const [pending, setPending] = useState(false)
  const debounceRef = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(debounceRef.current), [])

  const commit = useCallback((request: OperatingRoomFilterDTO) => {
    appliedRef.current = request
    setApplied(request)
    setPending(false)
  }, [])

  /**
   * Único camino a lo aplicado: valida las fechas y, si cambió algo, aplica ya (`immediate`) o después de `DEBOUNCE_MS`.
   */
  const applyFilters = useCallback((next: OperatingRoomFiltersState, immediate: boolean) => {
    window.clearTimeout(debounceRef.current)
    const validationErrors = validate(next)
    // Sin cambios no se vuelve a renderizar: un renderizado de más puede comerse el click sobre el calendario
    setErrors((current) => (sameErrors(current, validationErrors) ? current : validationErrors))
    const request = Object.keys(validationErrors).length > 0 ? null : toAppliedFilters(next)
    if (!request || sameApplied(request, appliedRef.current)) {
      setPending(false)
      return
    }
    if (immediate) {
      commit(request)
    } else {
      setPending(true)
      debounceRef.current = window.setTimeout(() => commit(request), DEBOUNCE_MS)
    }
  }, [commit])

  const setFiltersNow = useCallback((next: OperatingRoomFiltersState) => {
    filtersRef.current = next
    setFilters(next)
  }, [])

  /** Un combo de «Más filtros»: se guarda y se aplica con retardo. */
  const changeFilter = useCallback((patch: Partial<OperatingRoomSelectFilters>) => {
    const next = { ...filtersRef.current, ...patch }
    setFiltersNow(next)
    applyFilters(next, false)
  }, [setFiltersNow, applyFilters])

  /** Un chip, «Quitar todos» o una fila del agrupado: se aplica ya. */
  const patchFilters = useCallback((patch: Partial<OperatingRoomSelectFilters>) => {
    const next = { ...filtersRef.current, ...patch }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [setFiltersNow, applyFilters])

  /** Una fecha completa y válida (o vacía, con «Limpiar»): se guarda y se aplica ya. */
  const changeDate = useCallback((field: PeriodDateField, value: Date | null) => {
    const next = { ...filtersRef.current, [field]: value }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [setFiltersNow, applyFilters])

  /** Al salir de los campos de fecha o con Enter: se vuelve a validar y aplicar lo que haya (sin cambios, no hace nada). */
  const commitDates = useCallback(() => applyFilters(filtersRef.current, true), [applyFilters])

  /** «Período»: un atajo aplica su rango ya; «Personalizado» solo muestra las fechas (conservan el rango actual). */
  const changePeriod = useCallback((choice: PeriodChoice) => {
    setCustomPeriod(choice === 'custom')
    if (choice === 'custom') return
    const { from, to } = presetRange(choice, today)
    const next = { ...filtersRef.current, from, to }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [today, setFiltersNow, applyFilters])

  /** El período de antes de «Ver solo este día», para ofrecer «Volver a …». */
  const [rangeOrigin, setRangeOrigin] = useState<RangeOrigin | null>(null)

  /**
   * «Ver solo este día» (del panel del día): pasa a ser el período de la pantalla, como un período personalizado. Si ya
   * era ese no cambia nada; si no, queda el período anterior para «Volver a …» bajo los filtros.
   */
  const viewRange = useCallback((from: string, to: string) => {
    const current = filtersRef.current
    if (current.from && current.to && isoDate(current.from) === from && isoDate(current.to) === to) return
    const preset = customPeriod ? null : activePreset(current.from, current.to, today)
    let label = preset ? intl.formatMessage({ id: PERIOD_PRESET_NAME_ID[preset] }) : ''
    if (!label && current.from && current.to) {
      const withYear = periodNeedsYear(current.from, current.to, today)
      label = intl.formatMessage({ id: 'PeriodFieldRange' }, {
        from: formatShortDate(current.from, withYear),
        to: formatShortDate(current.to, withYear),
      })
    }
    setRangeOrigin({ previousFrom: current.from, previousTo: current.to, custom: customPeriod, label, from, to })
    setCustomPeriod(true)
    const next = { ...current, from: parseIsoDate(from), to: parseIsoDate(to) }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [customPeriod, today, intl, setFiltersNow, applyFilters])

  /** «Volver a {período anterior}»: devuelve las fechas de antes de «Ver solo este día». */
  const returnToPreviousRange = useCallback(() => {
    if (!rangeOrigin) return
    setCustomPeriod(rangeOrigin.custom)
    const next = { ...filtersRef.current, from: rangeOrigin.previousFrom, to: rangeOrigin.previousTo }
    setFiltersNow(next)
    applyFilters(next, true)
    setRangeOrigin(null)
  }, [rangeOrigin, setFiltersNow, applyFilters])

  /** El enlace «Volver a …» solo vale mientras se siga viendo el rango que salió de «Ver solo este día». */
  const originActive = rangeOrigin !== null && !!filters.from && !!filters.to
    && isoDate(filters.from) === rangeOrigin.from && isoDate(filters.to) === rangeOrigin.to
  useEffect(() => {
    if (rangeOrigin && !originActive) setRangeOrigin(null)
  }, [rangeOrigin, originActive])

  const period = periodChoiceOf(filters.from, filters.to, customPeriod, today)
  const rangeText = useMemo(
    () => (period === 'custom' ? '' : rangeTextOf(intl, filters.from, filters.to, today)),
    [period, filters.from, filters.to, today, intl],
  )

  return {
    filters,
    errors,
    period,
    rangeText,
    applied,
    pending,
    changeFilter,
    patchFilters,
    changeDate,
    commitDates,
    changePeriod,
    viewRange,
    /** De dónde se vino con «Ver solo este día» (con el nombre del período anterior para el enlace); null si no aplica. */
    rangeOrigin: originActive ? rangeOrigin : null,
    returnToPreviousRange,
  }
}

export type OperatingRoomFiltersData = ReturnType<typeof useOperatingRoomFilters>
